'use client';

import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Agenda, Employee, EventsSpeaker, Media } from '@/payload-types';
import { cn } from '@/utils/cn';

import styles from './EventAgenda.module.scss';

type Props = {
  item: NonNullable<Agenda['items']>[number];
};

export const EventAgendaItem: React.FC<Props> = ({ item }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const speakersImages = [] as Media[];
  const speakersNames = [] as string[];
  const speakersRoles = [] as string[];

  item.speakers?.map((speaker) => {
    const speakerImage =
      speaker.typeOfSpeaker === 'employee'
        ? ((speaker.employee as Employee).image as Media)
        : speaker.typeOfSpeaker === 'external'
          ? ((speaker.external as EventsSpeaker).image as Media)
          : null;

    if (speakerImage) {
      speakersImages.push(speakerImage);
    }

    const speakerName =
      speaker.typeOfSpeaker === 'employee'
        ? (speaker.employee as Employee).name
        : speaker.typeOfSpeaker === 'external'
          ? (speaker.external as EventsSpeaker).name
          : '';

    if (speakerName) {
      speakersNames.push(speakerName);
    }

    const speakerRole =
      speaker.typeOfSpeaker === 'employee'
        ? (speaker.employee as Employee).role
        : speaker.typeOfSpeaker === 'external'
          ? (speaker.external as EventsSpeaker).role
          : '';

    if (speakerRole) {
      speakersRoles.push(speakerRole);
    }
  });

  const handleModalOpen = () => {
    if (item.description === null) {
      return;
    }

    setModalOpen(true);

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  };

  const handleModalClose = () => {
    setModalOpen(false);

    // Unlock body scroll
    document.body.style.overflow = 'auto';
  };

  if (!item) {
    return null;
  }

  if (item.break) {
    return <div className={styles.break}>{item.breakLabel}</div>;
  }

  return (
    <>
      <div
        className={cn(
          styles.item,
          item.description && styles.withModal,
          speakersNames.length === 0 && styles.noSpeakers
        )}
        onClick={handleModalOpen}
      >
        {speakersImages.length > 0 && (
          <div className={styles.imageWrapper}>
            {speakersImages.map((speakerImage, index) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                loading='lazy'
                key={index}
                className={cn(
                  styles.image,
                  speakersImages.length > 1 && styles.multipleImages
                )}
                src={speakerImage.sizes?.squareSmall?.url || ''}
                alt={speakerImage.alt || ''}
              />
            ))}
          </div>
        )}
        {item.heading && <h3 className={styles.heading}>{item.heading}</h3>}
        {speakersNames.length > 0 && (
          <div className={styles.speaker}>
            {speakersNames.map((speakerName, index) => (
              <span key={index}>
                {speakerName}
                {speakersRoles[index] ? ` - ${speakersRoles[index]}` : ''}
              </span>
            ))}
          </div>
        )}
        <div className={styles.time}>
          {item.start &&
            new Date(item.start).toLocaleTimeString('da-DK', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          -
          {item.end &&
            new Date(item.end).toLocaleTimeString('da-DK', {
              hour: '2-digit',
              minute: '2-digit',
            })}
        </div>
      </div>
      {item.description && (
        <Modal
          clickHandler={handleModalClose}
          content={item.description}
          title={
            <div className={styles.modalTitle}>
              {speakersImages.length > 0 && (
                <div className={styles.imageWrapper}>
                  {speakersImages.map((speakerImage, index) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      loading='lazy'
                      key={index}
                      className={cn(
                        styles.image,
                        speakersImages.length > 1 && styles.multipleImages
                      )}
                      src={speakerImage.sizes?.squareSmall?.url || ''}
                      alt={speakerImage.alt || ''}
                    />
                  ))}
                </div>
              )}
              <div className={styles.content}>
                {item.heading && (
                  <h3 className={styles.modalHeading}>{item.heading}</h3>
                )}
                {speakersNames.length > 0 && (
                  <div className={styles.speaker}>
                    {speakersNames.map((speakerName, index) => (
                      <span key={index}>
                        {speakerName}
                        {speakersRoles[index]
                          ? ` - ${speakersRoles[index]}`
                          : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          }
          modalOpen={modalOpen}
        />
      )}
    </>
  );
};
